/**
 * App Source Files
 *
 * Browser helpers for turning uploaded Losi App source files into text.
 *
 * Primary responsibilities:
 * - Accept source files users naturally provide to app actions
 * - Route PDFs and screenshots through the Apps source extraction API
 *
 * Dependencies:
 * - Apps source text API
 */

export const APP_SOURCE_FILE_ACCEPT =
  'application/pdf,image/*,text/*,.pdf,.txt,.md,.markdown,.html,.htm,.json,.csv,.xml'

// --------------

export interface AppSourceFileScope {
  appId?: string | null
  workspaceId?: string | null
}

export interface AppSourceFileEvidence {
  imageDataUrl?: string
  text: string
}

const MAX_INLINE_SOURCE_IMAGE_BYTES = 6 * 1024 * 1024

// --------------

export async function readAppSourceFile(file: File | null, scope: AppSourceFileScope = {}) {
  const evidence = await readAppSourceFileEvidence(file, scope)
  return evidence?.text ?? null
}

// --------------

export async function readAppSourceFileEvidence(
  file: File | null,
  scope: AppSourceFileScope = {}
): Promise<AppSourceFileEvidence | null> {
  if (!file) return null

  if (isServerProcessedSourceFile(file)) {
    const imageDataUrl = await readSourceImageDataUrl(file)
    let text = ''

    try {
      text = await extractUploadedSourceText(file, scope)
    } catch (error) {
      if (!imageDataUrl) throw error
      text = `[Uploaded source: ${file.name}]\n\nVisual screenshot attached for review.`
    }

    return imageDataUrl ? { imageDataUrl, text } : { text }
  }

  if (!isReadableSourceFile(file)) {
    throw new Error('Upload a PDF, screenshot, text, Markdown, HTML, CSV, XML, or data file here.')
  }

  const text = (await file.text()).trim()
  if (!text) {
    throw new Error('That file did not contain readable text.')
  }

  return {
    text: [`[Uploaded source: ${file.name}]`, text.slice(0, 18_000)].join('\n\n')
  }
}

// --------------

async function extractUploadedSourceText(file: File, scope: AppSourceFileScope) {
  const formData = new FormData()
  formData.append('file', file)
  if (scope.appId) {
    formData.append('appId', scope.appId)
  }
  if (scope.workspaceId) {
    formData.append('workspaceId', scope.workspaceId)
  }

  const response = await fetch('/api/apps/source-text', {
    method: 'POST',
    body: formData
  })
  const payload = (await response.json().catch(() => null)) as {
    error?: string
    text?: string
  } | null

  if (!response.ok || !payload?.text) {
    throw new Error(payload?.error ?? 'Losi could not read that file.')
  }

  return payload.text
}

// --------------

function isServerProcessedSourceFile(file: File) {
  return (
    file.type === 'application/pdf' ||
    file.type.startsWith('image/') ||
    file.name.toLowerCase().endsWith('.pdf')
  )
}

// --------------

async function readSourceImageDataUrl(file: File) {
  if (!file.type.startsWith('image/')) return null
  if (file.size > MAX_INLINE_SOURCE_IMAGE_BYTES) return null

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Losi could not read that image.'))
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.readAsDataURL(file)
  })

  return dataUrl.startsWith('data:image/') ? dataUrl : null
}

// --------------

function isReadableSourceFile(file: File) {
  const name = file.name.toLowerCase()

  return (
    file.type.startsWith('text/') ||
    [
      '.csv',
      '.htm',
      '.html',
      '.json',
      '.markdown',
      '.md',
      '.txt',
      '.xml'
    ].some((extension) => name.endsWith(extension))
  )
}
